import { inject, Injectable } from '@angular/core';

import { AuthService, LoadingService } from '../store';
import { IHttpRequestResponse, IResponseData } from '@shared/types';
import { ToastService } from './toast.service';

export interface IApiCallOptions<T> {
  allSkip?: boolean;
  skipLoading?: boolean;
  /** Suppresses the toast only; the callbacks run either way. */
  skipAlert?: boolean;
  skipLogout?: boolean;
  onSuccess?: (res: IResponseData<T>) => void | Promise<void>;
  onError?: (err: IResponseData<T>) => void | Promise<void>;
}

/**
 * Port of the `useApiCall` hook. Same option surface and control flow:
 * global loading, 401 -> logout, a message on both branches.
 *
 * The messages are toasts rather than modal alerts now. React ran `onSuccess`
 * from the alert's dismiss handler, so the caller's follow-up -- almost always
 * a navigation -- waited on a tap and was dropped if the alert was dismissed
 * by its backdrop instead of its button. Nothing defers a callback here.
 */
@Injectable({ providedIn: 'root' })
export class ApiCallService {
  private readonly auth = inject(AuthService);
  private readonly toast = inject(ToastService);
  private readonly loading = inject(LoadingService);

  async callApi<T>(
    fn: () => Promise<IHttpRequestResponse<T>>,
    options?: IApiCallOptions<T>,
  ): Promise<IResponseData<T>> {
    const {
      allSkip = false,
      skipLoading = false,
      skipAlert = false,
      skipLogout = false,
      onSuccess,
      onError,
    } = options ?? {};

    const useLoading = !allSkip && !skipLoading;
    if (useLoading) {
      this.loading.show();
    }

    const response: IResponseData<T> = {
      statusCode: 500,
      message: 'Terjadi Kesalahan',
      data: undefined as T,
    };

    try {
      const { status, data } = await fn();

      if (status === 401 && !skipLogout) {
        await this.auth.logout();
      }

      if (allSkip) {
        return data;
      }

      if (status === 200) {
        if (!skipAlert) {
          this.toast.success(data.message || 'Sukses');
        }
        await onSuccess?.(data);
      } else {
        data.message = data.message || 'Terjadi kesalahan';
        if (!skipAlert) {
          this.toast.error(data.message);
        }
        await onError?.(data);
      }

      return data;
    } catch (error: unknown) {
      const err = error as { statusCode?: number; message?: string };
      response.statusCode = err?.statusCode ?? 500;
      response.message = err?.message ?? 'Terjadi kesalahan';

      if (!allSkip) {
        if (!skipAlert) {
          this.toast.error(response.message);
        }
        await onError?.(response);
      }

      return response;
    } finally {
      if (useLoading) {
        this.loading.hide();
      }
    }
  }
}
